import type { IMenuItemsPropsSDK } from '../../../common/sdk/props-factories/menuItemsPropsSDKFactory';
import { validateMenuItemsId } from './sdkUtils/validators/validateMenuItemsId';
import { validateMenuItemsTarget } from './sdkUtils/validators/validateMenuItemsTarget';
import { validateMenuItemsDepth } from './sdkUtils/validators/validateMenuItemsDepth';
import type { PageList } from './sdkUtils/menuUtils';
import { getLabel, getLink } from './sdkUtils/menuUtils';
import { InvalidLabelError, LinkTypeError } from './sdkUtils/errors';
import { assert } from '../../../common/sdk/assert';
import type { LinkUtils } from '@wix/thunderbolt-symbols';
import type { MenuDataItem } from '@wix/editor-elements-definitions';
import type { MenuItemType } from './Menu.types';

export const validators = [
  validateMenuItemsDepth(1),
  validateMenuItemsTarget,
  validateMenuItemsId,
];

// TODO: schema validation?
export const validateMenuItems = (menuItems: Array<MenuDataItem>) => {
  let isValid = true;
  isValid = validators.every(validator => validator(menuItems));
  return isValid;
};

const getMenuItems = (
  linkUtils: LinkUtils,
  pageList: PageList,
  items?: IMenuItemsPropsSDK['menuItems'],
) => {
  if (assert.isArray(items)) {
    return items.map((menuItem, i) =>
      createMenuDataItem(menuItem, i, linkUtils, pageList),
    );
  }
  return [];
};

export const createMenuDataItem = (
  menuDataItem: IMenuItemsPropsSDK['menuItems'][number],
  index: number,
  linkUtils: LinkUtils,
  pageList: PageList,
): IMenuItemsPropsSDK['menuItems'][number] => {
  const menuSdkItem: IMenuItemsPropsSDK['menuItems'][number] = {};

  try {
    const linkData = getLink({
      linkUtils,
      link: menuDataItem.link,
      target: menuDataItem.target || '_self',
    });

    if (linkData.href) {
      menuSdkItem.link = linkData.href;
      menuSdkItem.target = linkData.target || '_self';
    }
  } catch (error) {
    throw new LinkTypeError(menuDataItem.link || '', index);
  }

  const label = getLabel({
    label: menuDataItem.label,
    link: menuDataItem.link,
    pageList: pageList as PageList,
  });

  if (assert.isNil(label)) {
    throw new InvalidLabelError(index);
  }

  if (!assert.isNil(menuDataItem.id)) {
    menuSdkItem.id = menuDataItem.id;
  }

  menuSdkItem.label = label;

  if (!assert.isNil(menuDataItem.selected)) {
    menuSdkItem.selected = menuDataItem.selected;
  }

  return {
    ...menuSdkItem,
    menuItems: getMenuItems(linkUtils, pageList, menuDataItem.menuItems),
  };
};

/**
 * Calculate the index of the dropdown to open based on priority:
 * 1. Dropdown with most sub-items (item.items[].items)
 * 2. Dropdown with most items (item.items)
 * 3. First dropdown
 * @param items - Array of menu items
 * @returns Index of the dropdown to open, or -1 if no dropdown exists
 */
export const calculateDropdownIndexToOpen = (
  items: Array<MenuItemType> = [],
): number => {
  let selectedIndex = -1;
  let maxSubItemsCount = 0;
  let maxItemsCount = 0;
  let hasAnySubItems = false;

  items.forEach((item, index) => {
    if (item.items?.length) {
      const subItemsCount = item.items.reduce((sum, dropdownItem) => {
        return sum + (dropdownItem.items?.length || 0);
      }, 0);

      if (subItemsCount > 0) {
        hasAnySubItems = true;
        if (subItemsCount > maxSubItemsCount) {
          maxSubItemsCount = subItemsCount;
          selectedIndex = index;
        }
      }

      if (!hasAnySubItems && item.items.length > maxItemsCount) {
        maxItemsCount = item.items.length;
        selectedIndex = index;
      }
    }
  });

  return selectedIndex;
};

export const rootItemDepthLevel = 0;
export const subItemDepthLevel = 1;
export const subSubItemDepthLevel = 2;

export const getItemDepthSelector = (depth: number) =>
  `[data-item-depth="${depth}"]`;
